import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

interface AdsState {
  loading: boolean;
  gptLoading: boolean;
  playing: boolean;
  paused: boolean;
  volume: number;
  isFullscreen: boolean;
  metadata: any;
  error: string;
  url: string;
  contentType: string;
  data: { [key: string]: any };
  consent: boolean;
  prerollPlayed: boolean;
  adswizzConsent: boolean;
  hasCompanionAd: boolean;
  companionAdContent: string;
  isSkippable: boolean;
  skipTimeOffset: number;
  adCountdown: number;
}

// Get consent from localStorage if available
const getSavedConsent = (): boolean => {
  try {
    const savedConsent = localStorage.getItem('cookieConsent');
    return savedConsent === 'true';
  } catch (error) {
    // In case localStorage is not available (e.g., in incognito mode)
    console.error('Error accessing localStorage:', error);
    return false;
  }
};

const initialState: AdsState = {
  loading: false,
  gptLoading: true,
  playing: false,
  paused: false,
  volume: 0.5,
  isFullscreen: false,
  metadata: { title: "", name: "" },
  error: "",
  url: "",
  contentType: "",
  data: {},
  consent: getSavedConsent(),
  adswizzConsent: true,
  prerollPlayed: false,
  hasCompanionAd: false,
  companionAdContent: "",
  isSkippable: false,
  skipTimeOffset: 0,
  adCountdown: 0,
};

const slice = createSlice({
  name: "ads",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setGptLoading: (state, action: PayloadAction<boolean>) => {
      state.gptLoading = action.payload;
    },
    setPlaying: (state, action: PayloadAction<boolean>) => {
      state.playing = action.payload;
    },
    setPaused: (state, action: PayloadAction<boolean>) => {
      state.paused = action.payload;
    },
    setVolume: (state, action: PayloadAction<number>) => {
      state.volume = action.payload;
    },
    setIsFullscreen: (state, action: PayloadAction<boolean>) => {
      state.isFullscreen = action.payload;
    },
    setMetadata: (state, action: PayloadAction<object>) => {
      state.metadata = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setUrl: (state, action: PayloadAction<string>) => {
      state.url = action.payload;
    },
    setContentType: (state, action: PayloadAction<string>) => {
      state.contentType = action.payload;
    },
    setData: (state, action: PayloadAction<{ [key: string]: any }>) => {
      state.data = action.payload;
    },
    setConsent: (state, action: PayloadAction<boolean>) => {
      state.consent = action.payload;
    },
    setAdswizzConsent: (state, action: PayloadAction<boolean>) => {
      state.adswizzConsent = action.payload;
    },
    /**
     * Correctly spelled action. Preferred going forward.
     */
    setPrerollPlayed: (state, action: PayloadAction<boolean>) => {
      state.prerollPlayed = action.payload;
    },
    setHasCompanionAd: (state, action: PayloadAction<boolean>) => {
      state.hasCompanionAd = action.payload;
    },
    setCompanionAdContent: (state, action: PayloadAction<string>) => {
      state.companionAdContent = action.payload;
    },
    setIsSkippable: (state, action: PayloadAction<boolean>) => {
      state.isSkippable = action.payload;
    },
    setSkipTimeOffset: (state, action: PayloadAction<number>) => {
      state.skipTimeOffset = action.payload;
    },
    setAdCountdown: (state, action: PayloadAction<number>) => {
      state.adCountdown = action.payload;
    },
  },
});

export const {
  setLoading,
  setGptLoading,
  setPaused,
  setPlaying,
  setMetadata,
  setVolume,
  setIsFullscreen,
  setError,
  setUrl,
  setContentType,
  setData,
  setConsent,
  setAdswizzConsent,
  setPrerollPlayed,
  setHasCompanionAd,
  setCompanionAdContent,
  setIsSkippable,
  setSkipTimeOffset,
  setAdCountdown,
} = slice.actions;

export const selectLoading = (state: { ads: AdsState }) => state.ads.loading;

export const selectGptLoading = (state: { ads: AdsState }) =>
  state.ads.gptLoading;

export const selectPlaying = (state: { ads: AdsState }) => state.ads.playing;

export const selectPaused = (state: { ads: AdsState }) => state.ads.paused;

export const selectVolume = (state: { ads: AdsState }) => state.ads.volume;

export const selectIsFullscreen = (state: { ads: AdsState }) =>
  state.ads.isFullscreen;

export const selectMetadata = (state: { ads: AdsState }) => state.ads.metadata;

export const selectError = (state: { ads: AdsState }) => state.ads.error;

export const selectUrl = (state: { ads: AdsState }) => state.ads.url;
export const selectContentType = (state: { ads: AdsState }) =>
  state.ads.contentType;
export const selectData = (state: { ads: AdsState }) => state.ads.data;
export const selectConsent = (state: { ads: AdsState }) => state.ads.consent;
export const selectAdswizzConsent = (state: { ads: AdsState }) =>
  state.ads.adswizzConsent;
export const selectPrerollPlayed = (state: { ads: AdsState }) =>
  state.ads.prerollPlayed;

export const selectHasCompanionAd = (state: { ads: AdsState }) =>
  state.ads.hasCompanionAd;

export const selectCompanionAdContent = (state: { ads: AdsState }) =>
  state.ads.companionAdContent;

export const selectIsSkippable = (state: { ads: AdsState }) =>
  state.ads.isSkippable;

export const selectSkipTimeOffset = (state: { ads: AdsState }) =>
  state.ads.skipTimeOffset;

export const selectAdCountdown = (state: { ads: AdsState }) => state.ads.adCountdown;

export default slice.reducer;
